import { Plane, Users } from 'lucide-react';

export const FlightInfoSection = ({ data, config, t, formatDateTime, getServiceIcon }) => (
  <div className='pdf-section' style={{
    background: 'transparent',
    marginBottom: config.gap
  }}>
    <div style={{ 
      marginBottom: 0,
      fontFamily: config.fontFamily,
      background: config.sectionColor, // ← Changé de backgroundColor à sectionColor
      padding: '20px',
      borderRadius: '8px',
      border: `1px solid ${config.sectionColor}40`
    }}>
      {/* Titre de section avec configuration personnalisable */}
      <div style={{
        fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
        fontWeight: config.sectionTitle?.fontWeight || 'bold',
        color: config.sectionTitle?.color || config.primaryColor,
        marginBottom: 20,
        paddingBottom: 8,
        borderBottom: `2px solid ${config.sectionTitle?.borderColor || config.primaryColor}`,
        textTransform: config.sectionTitle?.textTransform || 'uppercase',
        letterSpacing: config.sectionTitle?.letterSpacing || '1px',
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        {getServiceIcon ? getServiceIcon('flight') : <Plane size={20} />}
        {data.name || t('flightInformation')}
      </div>

      {/* Informations générales - Personnalisable avec config.columns */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: config.gridGap || 20
        }}>
          {/* Airline */}
          {data.airline && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }}>
              <div style={{
                fontSize: config.infoLabelSize || 14,
                color: config.primaryColor, // ← Changé pour utiliser primaryColor
                fontWeight: config.infoLabelWeight || 500
              }}>
                {t('airline')}
              </div>
              <div style={{
                fontSize: config.infoValueSize || 16,
                color: config.textColor || '#1f2937', // ← Utilise textColor
                fontWeight: config.infoValueWeight || 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}>
                <Plane size={config.iconSize || 16} />
                {data.airline}
              </div>
            </div>
          )}

          {/* Type de voyage */}
          {data.trip && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }}>
              <div style={{
                fontSize: config.infoLabelSize || 14,
                color: config.primaryColor, // ← Changé pour utiliser primaryColor
                fontWeight: config.infoLabelWeight || 500
              }}>
                {t('tripType')}
              </div>
              <div style={{
                fontSize: config.infoValueSize || 16,
                color: config.textColor || '#1f2937', // ← Utilise textColor
                fontWeight: config.infoValueWeight || 600
              }}>
                {data.trip === 'roundtrip' ? t('roundTrip') : 
                 data.trip === 'oneway' ? t('oneWay') : data.trip}
              </div>
            </div>
          )}
        </div>

        {/* Passagers */}
        {(data.adult !== undefined || data.child !== undefined || data.infant !== undefined) && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            marginTop: 16
          }}>
            <div style={{
              fontSize: config.infoLabelSize || 14,
              color: config.primaryColor, // ← Changé pour utiliser primaryColor
              fontWeight: config.infoLabelWeight || 500
            }}>
              {t('passengers')}
            </div>
            <div style={{
              fontSize: config.infoValueSize || 16,
              color: config.textColor || '#1f2937', // ← Utilise textColor
              fontWeight: config.infoValueWeight || 600,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap'
            }}>
              {data.adult > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {t('adults')}: {data.adult}
                </span>
              )}
              {data.child > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ margin: '0 4px' }}>•</span>
                  {t('children')}: {data.child}
                </span>
              )}
              {data.infant > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ margin: '0 4px' }}>•</span>
                  {t('infants')}: {data.infant}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Itinéraires - Structure améliorée */}
      {data.itineraries && data.itineraries.length > 0 && (
        <div>
          {/* Titre des itinéraires */}
          {config.showItinerariesTitle !== false && (
            <div style={{
              fontSize: config.subsectionTitleSize || 16,
              fontWeight: config.subsectionTitleWeight || 600,
              color: config.secondaryColor, // ← Utilise secondaryColor
              marginBottom: 12
            }}>
              {t('itineraries')}
            </div>
          )}

          {data.itineraries.map((itinerary, index) => (
            <div key={index} style={{ 
              marginBottom: '24px', 
              padding: '16px', 
              background: config.backgroundColor, // ← Reste backgroundColor pour le contraste
              borderRadius: config.itineraryBorderRadius || '8px',
              border: `1px solid ${config.sectionColor}40` // ← Utilise sectionColor
            }}>
              {/* Header du segment - Personnalisable */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: `1px solid ${config.sectionColor}40` // ← Utilise sectionColor
              }}>
                <div>
                  <div style={{ 
                    fontSize: config.itineraryHeaderSize || 16, 
                    fontWeight: config.itineraryHeaderWeight || 600, 
                    color: config.textColor || '#1f2937' // ← Utilise textColor
                  }}>
                    {t('flight')} {index + 1}: {itinerary.departure?.code || itinerary.departure_airport_code} → {itinerary.arrival?.code || itinerary.arrival_airport_code}
                  </div>
                  {(itinerary.departure?.name || itinerary.arrival?.name) && (
                    <div style={{ 
                      fontSize: config.itinerarySubheaderSize || 14, 
                      color: config.secondaryColor // ← Utilise secondaryColor
                    }}>
                      {itinerary.departure?.name || ''} → {itinerary.arrival?.name || ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Segments de vol */}
              {itinerary.segments && itinerary.segments.map((segment, segIndex) => (
                <div key={segIndex} style={{ 
                  marginBottom: segIndex < itinerary.segments.length - 1 ? '20px' : '0',
                  ...(config.segmentSpacing && { marginBottom: config.segmentSpacing })
                }}>
                  {/* Header du vol - Personnalisable */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                    background: config.sectionColor, // ← Utilise sectionColor
                    padding: config.segmentHeaderPadding || '12px',
                    borderRadius: config.segmentHeaderBorderRadius || '6px'
                  }}>
                    <div>
                      <div style={{ 
                        fontSize: config.segmentHeaderSize || 16, 
                        fontWeight: config.segmentHeaderWeight || 600, 
                        color: config.primaryColor // ← Utilise primaryColor
                      }}>
                        {segment.airline?.code} {segment.flightNumber}
                      </div>
                      {segment.airline?.name && (
                        <div style={{ 
                          fontSize: config.segmentSubheaderSize || 14, 
                          color: config.secondaryColor // ← Utilise secondaryColor
                        }}>
                          {segment.airline.name}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ 
                        fontSize: config.durationSize || 14, 
                        fontWeight: config.durationWeight || 600, 
                        color: config.textColor || '#1f2937' // ← Utilise textColor
                      }}>
                        {t('duration')}: {segment.duration}
                      </div>
                    </div>
                  </div>

                  {/* Détails départ/arrivée - Personnalisable */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: config.flightDetailsLayout || '1fr auto 1fr',
                    gap: config.flightDetailsGap || '16px',
                    alignItems: 'center'
                  }}>
                    {/* Départ */}
                    <div>
                      <div style={{ 
                        fontSize: config.airportCodeSize || 16, 
                        fontWeight: config.airportCodeWeight || 600, 
                        color: config.textColor || '#1f2937' // ← Utilise textColor
                      }}>
                        {segment.departure?.code || segment.departure_airport_code}
                      </div>
                      {segment.departure?.name && (
                        <div style={{ 
                          fontSize: config.airportNameSize || 14, 
                          color: config.secondaryColor, // ← Utilise secondaryColor
                          marginBottom: '4px' 
                        }}>
                          {segment.departure.name}
                        </div>
                      )}
                      {segment.departure?.date && (
                        <div style={{ 
                          fontSize: config.dateTimeSize || 14, 
                          fontWeight: config.dateTimeWeight || 500, 
                          color: config.textColor || '#374151' // ← Utilise textColor
                        }}>
                          {formatDateTime(segment.departure.date)}
                        </div>
                      )}
                      {segment.departure?.terminal && (
                        <div style={{ 
                          fontSize: config.terminalSize || 13, 
                          color: config.secondaryColor, // ← Utilise secondaryColor
                          marginTop: '2px' 
                        }}>
                          {t('terminal')}: {segment.departure.terminal}
                        </div>
                      )}
                    </div>

                    {/* Flèche/avion - Personnalisable */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ 
                        width: config.planeIconSize || '40px', 
                        height: config.planeIconSize || '40px',
                        background: config.planeIconBackground || config.primaryColor,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto'
                      }}>
                        <Plane 
                          size={config.planeIconInnerSize || 20} 
                          color={config.planeIconColor || 'white'} 
                        />
                      </div>
                      {config.showDurationBelowPlane !== false && segment.duration && (
                        <div style={{ 
                          fontSize: config.durationBelowSize || 12, 
                          color: config.secondaryColor, // ← Utilise secondaryColor
                          marginTop: '4px' 
                        }}>
                          {segment.duration}
                        </div>
                      )}
                    </div>

                    {/* Arrivée */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ 
                        fontSize: config.airportCodeSize || 16, 
                        fontWeight: config.airportCodeWeight || 600, 
                        color: config.textColor || '#1f2937' // ← Utilise textColor
                      }}>
                        {segment.arrival?.code || segment.arrival_airport_code}
                      </div>
                      {segment.arrival?.name && (
                        <div style={{ 
                          fontSize: config.airportNameSize || 14, 
                          color: config.secondaryColor, // ← Utilise secondaryColor
                          marginBottom: '4px' 
                        }}>
                          {segment.arrival.name}
                        </div>
                      )}
                      {segment.arrival?.date && (
                        <div style={{ 
                          fontSize: config.dateTimeSize || 14, 
                          fontWeight: config.dateTimeWeight || 500, 
                          color: config.textColor || '#374151' // ← Utilise textColor
                        }}>
                          {formatDateTime(segment.arrival.date)}
                        </div>
                      )}
                      {segment.arrival?.terminal && (
                        <div style={{ 
                          fontSize: config.terminalSize || 13, 
                          color: config.secondaryColor, // ← Utilise secondaryColor
                          marginTop: '2px' 
                        }}>
                          {t('terminal')}: {segment.arrival.terminal}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Version simplifiée pour les anciennes données */}
      {(!data.itineraries || data.itineraries.length === 0) && data.departure_airport_code && data.arrival_airport_code && (
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          border: `1px solid ${config.sectionColor}40`, // ← Utilise sectionColor
          marginTop: 16
        }}>
          <thead>
            <tr style={{
              background: config.backgroundColor,
              borderBottom: `2px solid ${config.sectionColor}40` // ← Utilise sectionColor
            }}>
              <th style={{
                padding: config.tableCellPadding || '12px 16px',
                textAlign: 'left',
                fontWeight: config.tableHeaderWeight || 600,
                color: config.textColor || '#374151', // ← Utilise textColor
                fontSize: config.tableHeaderSize || `calc(${config.fontSize || 14}px * 1)`,
                textTransform: config.tableHeaderTextTransform || 'uppercase'
              }}>
                {t('departure')}
              </th>
              <th style={{
                padding: config.tableCellPadding || '12px 16px',
                textAlign: 'left',
                fontWeight: config.tableHeaderWeight || 600,
                color: config.textColor || '#374151', // ← Utilise textColor
                fontSize: config.tableHeaderSize || `calc(${config.fontSize || 14}px * 1)`,
                textTransform: config.tableHeaderTextTransform || 'uppercase'
              }}>
                {t('arrival')}
              </th>
              <th style={{
                padding: config.tableCellPadding || '12px 16px',
                textAlign: 'left',
                fontWeight: config.tableHeaderWeight || 600,
                color: config.textColor || '#374151', // ← Utilise textColor
                fontSize: config.tableHeaderSize || `calc(${config.fontSize || 14}px * 1)`,
                textTransform: config.tableHeaderTextTransform || 'uppercase'
              }}>
                {t('duration')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr style={{
              borderBottom: `1px solid ${config.sectionColor}40` // ← Utilise sectionColor
            }}>
              <td style={{ 
                padding: config.tableCellPadding || '12px 16px', 
                fontSize: config.tableCellSize || `calc(${config.fontSize || 14}px * 1)`, 
                color: config.textColor || '#374151' // ← Utilise textColor
              }}>
                {data.departure_airport_code || data.departure}
              </td>
              <td style={{ 
                padding: config.tableCellPadding || '12px 16px', 
                fontSize: config.tableCellSize || `calc(${config.fontSize || 14}px * 1)`, 
                color: config.textColor || '#374151' // ← Utilise textColor
              }}>
                {data.arrival_airport_code || data.arrival}
              </td>
              <td style={{ 
                padding: config.tableCellPadding || '12px 16px', 
                fontSize: config.tableCellSize || `calc(${config.fontSize || 14}px * 1)`, 
                color: config.textColor || '#374151' // ← Utilise textColor
              }}>
                {data.duration}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  </div>
);

// Mettre à jour le mapping SectionComponents pour inclure tous les composants
export const FlightSectionComponents = {
  FlightInfoSection,
};

// Fonction utilitaire pour obtenir un composant par nom
export const getSectionComponent = (componentName) => {
  return FlightSectionComponents[componentName] || null;
};